import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { CookieService } from 'ngx-cookie-service';
import { SharedService } from 'src/app/shared.service';
import { B2BService } from 'src/app/b2-b.service';
import { TranslateService } from '@ngx-translate/core';
import { Router, ActivatedRoute } from "@angular/router";
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
  selector: 'app-register',
  templateUrl: './register.component.html',
  styleUrls: ['./register.component.css']
})
export class RegisterComponent implements OnInit {

  // Multi-step flow control
  currentStep: number = 1; // Steps: 1=Register/Login, 2=Billing, 3=Shipping, 4=Agents

  // Step A: Registration/Login
  User: any;
  RememberMeData = {};
  rememberMe: boolean = false;
  isRegistering: boolean = false;
  showExistingUserLogin: boolean = false;
  existingUserEmail: string = '';
  loginUserName: string = '';
  loginPassword: string = '';
  logoImage: string = '';
  LoginResponse: any = {};
  ShowMFA: boolean = false;
  MFAToken: string = '';
  hide: boolean = true;
  hideCPassword: boolean = true;
  loading: boolean = false;

  // Step B: Billing Information (Partner)
  Partner: any = {
    BillingAddress: {},
    Bank: {},
    ContactInfo: {}
  };
  Countries: any[] = [];
  Banks: any[] = [];
  showBillingCountryDropdown: boolean = false;
  showBankDropdown: boolean = false;

  // Searchable dropdown filters
  billingCountrySearchTerm: string = '';
  shippingCountrySearchTerm: string = '';
  bankSearchTerm: string = '';

  // Add New Bank feature
  showAddBankForm: boolean = false;
  newBankName: string = '';
  newBankSwift: string = '';
  addingBank: boolean = false;

  // Step C: Shipping Information
  CurrentShippingAddress: any = {};
  showShippingCountryDropdown: boolean = false;

  // Shipping Address List & Popup
  selectedShippingAddressIndex: number = -1;  // Index of selected address in list
  showShippingAddressPopup: boolean = false;  // Controls popup visibility
  editingShippingAddress: any = {};           // Address being edited in popup
  isNewShippingAddress: boolean = false;      // True if adding new, false if editing
  savingShippingAddress: boolean = false;     // Loading state for save operation
  showPopupShippingCountryDropdown: boolean = false;  // Country dropdown in popup
  popupShippingCountrySearchTerm: string = '';        // Search term for popup country dropdown

  // Step D: Agents Information
  PartnerAgents: any[] = [];
  PartnerAgent: any = null;
  Departments: any[] = [];
  ShippingAddresses: any[] = [];
  showAgentForm: boolean = false;
  showAgentShippingDropdown: boolean = false;
  showDepartmentDropdown: boolean = false;
  isEditingAgent: boolean = false;
  editingAgentIndex: number = -1;

  // Agent Notification Phases
  showDefaultPhasesPopup: boolean = false;
  selectedDefaultPhaseAgent: any = null;
  defaultPhasesMasterData: any[] = [];
  selectedDefaultPhaseIDs: number[] = [];
  savingDefaultPhases: boolean = false;

  // Default Company (loaded from API)
  DefaultCompany: any = null;

  // Session Storage Key
  private readonly SESSION_KEY = 'b2b_register_session';

  // Static System User for API calls before user is authenticated
  // Used for loading master data (Countries, Banks) during registration flow
  private readonly SYSTEM_USER = {
    Id: 'd0a6fe45467345caa8d046569f9a41687776e07d9d864d588912d32ebafd80e4',
    Email: 'office@foxline.ro'
  };

  // Restart confirmation
  showRestartConfirm: boolean = false;

  // Existing Partner Mode (loaded from API)
  isExistingPartner: boolean = false;

  // B2B Registration Link params (admin-generated link)
  b2bLinkPartnerID: string = null;
  b2bLinkAdminUserID: string = null;
  b2bLinkCompanyID: number = null;
  b2bLinkExpired: boolean = false;

  // Domain-Based Onboarding
  showCheckEmail: boolean = false;
  showPartnerSelection: boolean = false;
  showRegistrationComplete: boolean = false;
  domainPartners: any[] = [];
  DomainDBID: number = 0;

  // Registration Form
  signin: FormGroup = new FormGroup({
    email: new FormControl('', [Validators.email, Validators.required]),
    password: new FormControl('', [Validators.required, Validators.minLength(3)]),
    confirmPassword: new FormControl('', [Validators.required, Validators.minLength(3)]),
    username: new FormControl('', [Validators.required, Validators.minLength(3)]),
    phone: new FormControl('', [Validators.required, Validators.minLength(3)]),
    name: new FormControl('', [Validators.required, Validators.minLength(3)]),
  });

  get emailInput() { return this.signin.get('email'); }
  get passwordInput() { return this.signin.get('password'); }
  get confirmPasswordInput() { return this.signin.get('confirmPassword'); }
  get usernameInput() { return this.signin.get('username'); }
  get nameInput() { return this.signin.get('name'); }
  get phoneInput() { return this.signin.get('phone'); }

  constructor(
    private service: SharedService,
    private b2bService: B2BService,
    private cookieService: CookieService,
    public translate: TranslateService,
    private router: Router,
    private route: ActivatedRoute,
    private _snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef
  ) {
    translate.addLangs(['en', 'ro', 'de', 'es', 'fr', 'it', 'pl', 'pt']);
    translate.setDefaultLang('en');
    const browserLang = this.getLanguage();
    translate.use(browserLang.match(/en|ro|de|es|fr|it|pl|pt/) ? browserLang : 'en');
  }

  getLanguage(): string {
    if (localStorage) {
      return localStorage['language'] || "";
    }
    return "";
  }

  setLanguage(language: string) {
    if (localStorage) {
      localStorage['language'] = language;
    }
  }

  ngOnInit(): void {
    this.rememberMe = false;
    this.isRegistering = false;
    this.currentStep = 1;

    let RememberMeJSON: string = this.cookieService.get('RememberUser');
    if (RememberMeJSON !== undefined && RememberMeJSON != null && RememberMeJSON.length) {
      this.RememberMeData = JSON.parse(RememberMeJSON);
      this.rememberMe = true;
      this.loginUserName = this.RememberMeData["username"];
      this.loginPassword = this.RememberMeData["password"];
    }

    this.logoImage = this.service.ImagesServiceUrl + "/B2B/foxline_automation_icon.png";
    this.SetDefaultLanguage();

    // Restore session if exists (must be before loadMasterData since it restores User)
    this.restoreSession();

    // Always load master data (Countries, Banks) - use SYSTEM_USER if no real user
    // This ensures dropdowns are populated even during initial registration
    this.loadMasterData();

    // Load default company (ID: 1) from API
    this.loadDefaultCompany();

    // Deep-link handling: check for ?id= parameter (domain verification via email link)
    // OR ?pid=&aid=&cid= (admin-generated B2B registration link)
    this.route.queryParams.subscribe(params => {
      const uniqueCode = params['id'];
      const uid = params['uid'];

      // B2B Registration Link (admin-generated): ?vxrtmkd=...&bnqfwlz=...&jpthscx=...&kwdrmvf=...
      const vxrtmkd = params['vxrtmkd']; // vxrtmkd = partner ID
      const bnqfwlz = params['bnqfwlz']; // bnqfwlz = admin user ID
      const jpthscx = params['jpthscx']; // jpthscx = company ID
      const kwdrmvf = params['kwdrmvf']; // kwdrmvf = link generation date
      if (vxrtmkd && bnqfwlz && jpthscx) {
        try {
          // Check expiry first (3 days max)
          if (kwdrmvf) {
            const decoded = atob(kwdrmvf); // dd-MM-yyyy
            const parts = decoded.split('-');
            const linkDate = new Date(Number(parts[2]), Number(parts[1]) - 1, Number(parts[0]));
            const now = new Date();
            const diffMs = now.getTime() - linkDate.getTime();
            const diffDays = diffMs / (1000 * 60 * 60 * 24);
            if (diffDays > 3) {
              this.b2bLinkExpired = true;
              return;
            }
          }

          this.b2bLinkPartnerID = atob(vxrtmkd);   // decoded partner ID
          this.b2bLinkAdminUserID = atob(bnqfwlz);  // decoded admin user ID
          this.b2bLinkCompanyID = Number(atob(jpthscx)); // decoded company ID
        } catch (e) {
          console.error('[RegisterComponent] Failed to decode registration link params');
        }
        return;
      }

      if (uniqueCode && uniqueCode.length > 0) {
        // If no User in memory/localStorage, recover from uid before verifying
        if (!this.User?.Id && uid && uid.length > 0) {
          this.recoverUserAndVerify(uid, uniqueCode);
        } else {
          this.handleDeepLinkVerification(uniqueCode);
        }
      }
    });
  }

  loadDefaultCompany() {
    var request = {
      CompanyID: 1,
      User: this.getEffectiveUser(),
      Language: this.getLanguage()
    };

    this.service.GetCompanyByID(request).subscribe(response => {
      if (response["Status"] == 1) {
        this.DefaultCompany = response["Company"];
      }
    });
  }

  SetDefaultLanguage() {
    if (this.getLanguage() == '') {
      this.setLanguage("en");
    }
  }

  DeleteRememberedCredentials() {
    if (!this.rememberMe == false) {
      this.cookieService.delete("RememberUser", "/");
    }
  }

  openSnackBar(message: string, action: string, designClass: string) {
    this._snackBar.open(message, action, {
      duration: 5000,
      panelClass: [designClass]
    });
  }

  /**
   * Returns the effective user for API calls.
   * Uses SYSTEM_USER when no real user is authenticated (during registration flow).
   */
  getEffectiveUser(): any {
    return this.User || this.SYSTEM_USER;
  }

  // ============= STEP A: REGISTRATION / LOGIN =============

  RegisterNewUser() {
    var request = {
      Email: this.signin.controls['email'].value,
      Password: this.signin.controls['password'].value,
      ConfirmPassword: this.signin.controls['confirmPassword'].value,
      username: this.signin.controls['username'].value,
      phone: this.signin.controls['phone'].value,
      UIName: this.signin.controls['name'].value,
      Language: this.getLanguage()
    };

    this.loading = true;

    this.service.RegisterNewUser(request).subscribe(response => {
      if (response["Status"] != 1) {
        // Check if user already exists (Status code for existing user)
        if (response["Status"] == -2 || (response["Message"] && response["Message"].toLowerCase().includes('exist'))) {
          this.showExistingUserLogin = true;
          this.existingUserEmail = this.signin.controls['email'].value;
          this.loginUserName = this.signin.controls['email'].value;
          this.loginPassword = '';
          this.isRegistering = false;
        } else {
          this.openSnackBar(response["Message"], "OK", "alert-info-snackbar");
        }
      } else {
        // Registration successful - auto login
        this.loginUserName = this.signin.controls['email'].value;
        this.loginPassword = this.signin.controls['password'].value;
        this.rememberMe = true;
        this.LoginAfterRegistration();
      }
      this.loading = false;
    });
  }

  LoginAfterRegistration() {
    this.LoginResponse = {};

    var request = {
      username: this.loginUserName,
      email: this.loginUserName,
      password: this.loginPassword,
      Language: this.getLanguage()
    };

    this.loading = true;

    this.service.Login(request).subscribe(response => {
      if (this.rememberMe) {
        this.cookieService.set('RememberUser', JSON.stringify(request), 5, "/");
      }

      if (response["Status"] != 1) {
        this.openSnackBar(response["Message"], "OK", "alert-info-snackbar");
        this.loading = false;
      } else {
        this.User = response["User"];
        this.LoginResponse["Status"] = response["Status"];
        this.LoginResponse["Message"] = response["Message"];
        this.setLanguage(this.User.Settings.Language.Name.toLowerCase());
        this.translate.use(this.User.Settings.Language.Name.toLowerCase());
        localStorage.setItem('User', JSON.stringify(this.User));

        // Automatically assign Customer role for Company 1 to newly registered B2B users
        this.assignCustomerRoleForCompany1();

        // Check for existing partner before proceeding
        this.checkExistingPartnerAndProceed();
      }
    });
  }

  /**
   * Assigns the "Customer" role (eUserRoles.user = 0) for Company ID 1 to the newly registered user.
   * This ensures B2B users can access Company 1 immediately after registration.
   */
  assignCustomerRoleForCompany1() {
    // Customer role ID = 0 (eUserRoles.user in backend)
    const customerRole = { ID: 0, Meaning: 'Customer' };
    // Customer department ID = -1 (eDepartment.Customer in backend)
    const customerDepartment = { ID: -1, Name: 'Customer' };
    const companyId = 1;

    // Ensure Rights object exists with proper structure
    if (!this.User.Rights) {
      this.User.Rights = {
        MainRole: customerRole,
        AssignedCompanies: [],
        AssignedRoles: [],
        URLs: [],
        AccessErrors: []
      };
    }

    // Set MainRole to Customer
    this.User.Rights.MainRole = customerRole;

    // Ensure AssignedCompanies has Company 1 with Customer role and department
    if (!this.User.Rights.AssignedCompanies) {
      this.User.Rights.AssignedCompanies = [];
    }

    // Check if Company 1 already exists in AssignedCompanies
    let company1 = this.User.Rights.AssignedCompanies.find(c => c.ID === companyId);
    if (!company1) {
      // Add Company 1 with Customer role and department (use DefaultCompany if loaded, otherwise create minimal)
      company1 = this.DefaultCompany
        ? { ...this.DefaultCompany, MainRole: customerRole, Department: customerDepartment }
        : { ID: companyId, Name: 'Foxline SRL', MainRole: customerRole, Department: customerDepartment };
      this.User.Rights.AssignedCompanies.push(company1);
    } else {
      // Update existing Company 1 with Customer role and department
      company1.MainRole = customerRole;
      company1.Department = customerDepartment;
    }

    // Call API to persist the role assignment
    var rightsRequest = {
      Id: this.User.Id,
      Rights: this.User.Rights,
      ChangingCompanyID: companyId,
      Language: this.getLanguage(),
      User: this.SYSTEM_USER  // Use system user as the performing user
    };

    this.service.UpdateUserRights(rightsRequest).subscribe(response => {
      if (response["Status"] == 1) {
        // Update local storage with updated user rights
        localStorage.setItem('User', JSON.stringify(this.User));
        console.log('[RegisterComponent] Customer role assigned successfully for Company 1');
      } else {
        console.warn('[RegisterComponent] Failed to assign Customer role:', response["Message"]);
      }
    }, error => {
      console.warn('[RegisterComponent] Error assigning Customer role:', error);
    });
  }

  Login() {
    this.LoginResponse = {};

    var request = {
      username: this.loginUserName,
      email: this.loginUserName,
      password: this.loginPassword,
      Language: this.getLanguage()
    };

    this.loading = true;

    this.service.Login(request).subscribe(response => {
      if (this.rememberMe) {
        this.cookieService.set('RememberUser', JSON.stringify(request), 5, "/");
      }

      if (response["Status"] != 1) {
        this.openSnackBar(response["Message"], "OK", "alert-info-snackbar");
        this.loading = false;
      } else {
        this.User = response["User"];
        this.LoginResponse["Status"] = response["Status"];
        this.LoginResponse["Message"] = response["Message"];
        this.setLanguage(this.User.Settings.Language.Name.toLowerCase());
        this.translate.use(this.User.Settings.Language.Name.toLowerCase());
        localStorage.setItem('User', JSON.stringify(this.User));

        // Check for existing partner before proceeding
        this.checkExistingPartnerAndProceed();
      }
    });
  }

  VerifyMFAToken() {
    var request = {
      User: this.User,
      Language: this.getLanguage(),
      Token: this.MFAToken
    };

    this.loading = true;

    this.service.VerifyMFAToken(request).subscribe(response => {
      if (response["Status"] != 1) {
        this.openSnackBar(response["Message"], "OK", "alert-info-snackbar");
        this.loading = false;
      } else {
        this.cookieService.set('MFAuthToken', JSON.stringify(true), 5, "/");
        localStorage.setItem('User', JSON.stringify(this.User));
        // After MFA, check for existing partner / domain verification
        this.checkExistingPartnerAndProceed();
      }
    });
  }

  SendRecoverPasswordEmail() {
    var request = {
      UserName: this.loginUserName,
      Language: this.getLanguage()
    };

    this.loading = true;

    this.service.SendRecoverPasswordEmail(request).subscribe(response => {
      if (response["Status"] != 1) {
        this.openSnackBar(response["Message"], "OK", "alert-info-snackbar");
      } else {
        this.openSnackBar(response["Message"], "OK", "light-info-snackbar");
      }
      this.loading = false;
    });
  }

  // ============= CHECK EXISTING PARTNER =============

  checkExistingPartnerAndProceed() {
    // ✅ If admin-generated B2B registration link, fetch partner by ID directly
    if (this.b2bLinkPartnerID) {
      this.loadPartnerFromB2BLink();
      return;
    }

    var request = {
      User: this.User,
      Language: this.getLanguage() || 'en'
    };

    this.b2bService.GetB2BUserConnectedPartner(request).subscribe(response => {
      if (response["Status"] == 1 && response["Partner"]) {
        // Existing partner found - pre-populate form
        this.Partner = response["Partner"];
        this.isExistingPartner = true;

        // Ensure nested objects exist
        if (!this.Partner.BillingAddress) {
          this.Partner.BillingAddress = {};
        }
        if (!this.Partner.Bank) {
          this.Partner.Bank = {};
        }
        if (!this.Partner.ContactInfo) {
          this.Partner.ContactInfo = {};
        }

        // Pre-populate shipping addresses if available
        if (this.Partner.ShippingAddresses && this.Partner.ShippingAddresses.length > 0) {
          this.ShippingAddresses = this.Partner.ShippingAddresses;
          this.CurrentShippingAddress = { ...this.Partner.ShippingAddresses[0] };
        }

        // Pre-populate agents if available from Partner object
        if (this.Partner.PartnerAgents && this.Partner.PartnerAgents.length > 0) {
          this.PartnerAgents = this.Partner.PartnerAgents;
        }

        // Fetch contractual conditions for this partner
        this.getPartnerContractualConditions();

        // Load master data (Countries, Banks) for the form dropdowns
        this.loadMasterData();

        // Fetch partner agents separately (in case they're not included in Partner object)
        this.getPartnerAgents();

        // Determine which step to go to based on existing data
        this.determineStepBasedOnPartnerData();
      } else {
        // No existing partner - check domain for onboarding flow
        this.isExistingPartner = false;
        this.checkDomainAndSendVerification();
      }
    }, () => {
      this.isExistingPartner = false;
      this.checkDomainAndSendVerification();
    });
  }

  // ============= B2B REGISTRATION LINK (ADMIN-GENERATED) =============

  loadPartnerFromB2BLink() {
    this.loading = true;

    var request = {
      User: this.SYSTEM_USER,
      Language: this.getLanguage() || 'en',
      PartnerID: this.b2bLinkPartnerID,
      WithAllData: true
    };

    this.service.GetPartner(request).subscribe(response => {
      if (response["Status"] == 1 && response["Partner"]) {
        this.Partner = response["Partner"];
        this.isExistingPartner = true;

        // Ensure nested objects exist
        if (!this.Partner.BillingAddress) this.Partner.BillingAddress = {};
        if (!this.Partner.Bank) this.Partner.Bank = {};
        if (!this.Partner.ContactInfo) this.Partner.ContactInfo = {};

        // Pre-populate shipping addresses if available
        if (this.Partner.ShippingAddresses?.length > 0) {
          this.ShippingAddresses = this.Partner.ShippingAddresses;
          this.CurrentShippingAddress = { ...this.Partner.ShippingAddresses[0] };
        }

        // Pre-populate agents if available
        if (this.Partner.PartnerAgents?.length > 0) {
          this.PartnerAgents = this.Partner.PartnerAgents;
        }

        this.loadMasterData();
        this.getPartnerAgents();
        this.getPartnerContractualConditions();

        // Assign customer role for the specific company from the link
        if (this.b2bLinkCompanyID) {
          this.assignCustomerRoleForCompany(this.b2bLinkCompanyID);
        }

        this.determineStepBasedOnPartnerData();

        console.log('[RegisterComponent] Partner loaded from B2B link:', this.Partner.Name);
      } else {
        console.warn('[RegisterComponent] Failed to load partner from B2B link');
        this.isExistingPartner = false;
        this.proceedToStepB();
      }
      this.loading = false;
    }, () => {
      this.loading = false;
      this.isExistingPartner = false;
      this.proceedToStepB();
    });
  }

  /**
   * Assigns the "Customer" role for a specific Company ID.
   * Used by admin-generated B2B registration links with custom company ID.
   */
  assignCustomerRoleForCompany(companyId: number) {
    const customerRole = { ID: 0, Meaning: 'Customer' };
    const customerDepartment = { ID: -1, Name: 'Customer' };

    if (!this.User.Rights) {
      this.User.Rights = { MainRole: customerRole, AssignedCompanies: [], AssignedRoles: [], URLs: [], AccessErrors: [] };
    }

    this.User.Rights.MainRole = customerRole;

    if (!this.User.Rights.AssignedCompanies) {
      this.User.Rights.AssignedCompanies = [];
    }

    let company = this.User.Rights.AssignedCompanies.find((c: any) => c.ID === companyId);
    if (!company) {
      company = { ID: companyId, MainRole: customerRole, Department: customerDepartment, IsSelected: true };
      this.User.Rights.AssignedCompanies.push(company);
    } else {
      company.MainRole = customerRole;
      company.Department = customerDepartment;
    }

    var rightsRequest = {
      Id: this.User.Id,
      Rights: this.User.Rights,
      ChangingCompanyID: companyId,
      Language: this.getLanguage(),
      User: this.SYSTEM_USER
    };

    this.service.UpdateUserRights(rightsRequest).subscribe(response => {
      if (response["Status"] == 1) {
        localStorage.setItem('User', JSON.stringify(this.User));
        console.log('[RegisterComponent] Customer role assigned for Company', companyId);
      }
    });
  }

  // ============= DOMAIN-BASED ONBOARDING =============

  checkDomainAndSendVerification() {
    if (!this.User?.Email) {
      this.proceedToStepB();
      return;
    }

    this.loading = true;

    // First get SMTP configs for Company 1 to send verification email
    var smtpRequest = {
      User: this.getEffectiveUser(),
      Language: this.getLanguage(),
      CompanyID: 1
    };

    this.service.GetCompanySMTPConfigs(smtpRequest).subscribe(smtpResponse => {
      if (smtpResponse["Status"] != 1 || !smtpResponse["SMTPConfigs"]?.length) {
        // No SMTP config available - proceed normally
        this.loading = false;
        this.proceedToStepB();
        return;
      }

      var smtpConfig = smtpResponse["SMTPConfigs"][0];

      // Get SMTP emails for this config
      var emailRequest = {
        User: this.getEffectiveUser(),
        Language: this.getLanguage(),
        SMTPID: smtpConfig.ID
      };

      this.service.GetCompanySMTPEmails(emailRequest).subscribe(emailResponse => {
        if (emailResponse["Status"] != 1 || !emailResponse["Emails"]?.length) {
          this.loading = false;
          this.proceedToStepB();
          return;
        }

        var smtpEmail = emailResponse["Emails"].filter(x => x.IsDefault == true)[0] || emailResponse["Emails"][0];

        // Now call CheckDomainAndSendVerificationEmail
        var domainRequest = {
          User: this.getEffectiveUser(),
          Language: this.getLanguage(),
          Email: this.User.Email,
          BaseURL: window.location.origin,
          SMTPServer: smtpConfig.SMTPServer,
          SMTPPort: smtpConfig.Port,
          SMTPEmail: smtpEmail.Email,
          SMTPPassword: smtpEmail.Password,
          SMTPDisplayTitle: this.DefaultCompany?.Name || 'Foxline',
          UserID: this.User.Id
        };

        this.service.CheckDomainAndSendVerificationEmail(domainRequest).subscribe(response => {
          this.loading = false;
          if (response["Status"] != 1) {
            this.openSnackBar(response["Message"], "OK", "alert-info-snackbar");
            this.proceedToStepB();
            return;
          }

          if (response["VerificationRequired"] === true) {
            // Partners found for domain - email sent, show "check email" message
            this.showCheckEmail = true;
          } else if (response["NoDomainFound"] === true) {
            // No domain or domain without partners - store DomainDBID and proceed normally
            this.DomainDBID = response["DomainDBID"];
            this.proceedToStepB();
          } else {
            this.proceedToStepB();
          }
        }, () => {
          this.loading = false;
          this.proceedToStepB();
        });
      }, () => {
        this.loading = false;
        this.proceedToStepB();
      });
    }, () => {
      this.loading = false;
      this.proceedToStepB();
    });
  }

  /**
   * Recovers the user session from the backend using the uid from the verification link,
   * stores it in localStorage, and then proceeds with domain verification.
   */
  recoverUserAndVerify(uid: string, uniqueCode: string) {
    this.loading = true;

    var request = {
      id: uid,
      User: this.getEffectiveUser(),
      Language: this.getLanguage()
    };

    this.service.GetUser(request).subscribe(response => {
      if (response["Status"] == 1 && response["User"]) {
        this.User = response["User"];
        localStorage.setItem('User', JSON.stringify(this.User));
        console.log('[RegisterComponent] User recovered from uid:', this.User.Id);
      }
      this.loading = false;
      this.handleDeepLinkVerification(uniqueCode);
    }, () => {
      this.loading = false;
      // Even if user recovery fails, attempt verification with whatever user context we have
      this.handleDeepLinkVerification(uniqueCode);
    });
  }

  handleDeepLinkVerification(uniqueCode: string) {
    this.loading = true;

    var request = {
      User: this.getEffectiveUser(),
      Language: this.getLanguage(),
      UniqueCode: uniqueCode
    };

    this.service.GetPartnersByDomainUniqueCode(request).subscribe(response => {
      this.loading = false;
      if (response["Status"] != 1) {
        this.openSnackBar(response["Message"], "OK", "alert-info-snackbar");
        return;
      }

      var partners = response["Partners"] || [];
      var domain = response["Domain"];

      if (domain) {
        this.DomainDBID = domain.DBID;
      }

      if (partners.length >= 1) {
        // Auto-select first partner (oldest by EventRegister — most likely the correct one)
        this.selectDomainPartner(partners[0]);
      } else {
        // No partners - proceed to billing
        this.proceedToStepB();
      }
    }, () => {
      this.loading = false;
      this.openSnackBar(this.translate.instant('AnErrorOccurred'), "OK", "alert-info-snackbar");
    });
  }

  selectDomainPartner(partner: any) {
    this.Partner = partner;
    this.isExistingPartner = true;
    this.showPartnerSelection = false;

    // Ensure nested objects exist
    if (!this.Partner.BillingAddress) {
      this.Partner.BillingAddress = {};
    }
    if (!this.Partner.Bank) {
      this.Partner.Bank = {};
    }
    if (!this.Partner.ContactInfo) {
      this.Partner.ContactInfo = {};
    }

    // Pre-populate shipping addresses if available
    if (this.Partner.ShippingAddresses && this.Partner.ShippingAddresses.length > 0) {
      this.ShippingAddresses = this.Partner.ShippingAddresses;
      this.CurrentShippingAddress = { ...this.Partner.ShippingAddresses[0] };
    }

    // Pre-populate agents if available
    if (this.Partner.PartnerAgents && this.Partner.PartnerAgents.length > 0) {
      this.PartnerAgents = this.Partner.PartnerAgents;
    }

    this.loadMasterData();
    this.determineStepBasedOnPartnerData();
  }

  getPartnerContractualConditions() {
    if (!this.Partner?.ID) return;

    var request = {
      User: this.User,
      Language: this.getLanguage() || 'en',
      PartnerID: this.Partner.ID
    };

    this.service.GetPartnerContractualConditions(request).subscribe(response => {
      if (response["Status"] == 1) {
        this.Partner.ContractualConditions = response["ContractualConditions"];
      }
    });
  }

  getPartnerAgents() {
    if (!this.Partner?.ID) return;

    var request = {
      User: this.User,
      Language: this.getLanguage() || 'en',
      PartnerID: this.Partner.ID
    };

    this.service.GetPartnerAgents(request).subscribe(response => {
      if (response["Status"] == 1) {
        this.PartnerAgents = response["PartnerAgents"] || [];
        // Also store in Partner object for consistency
        this.Partner.PartnerAgents = this.PartnerAgents;
      }
    });
  }

  determineStepBasedOnPartnerData() {
    // For existing partners: ALWAYS start at Billing tab (Step 2)
    // They can freely navigate to other tabs from there

    // Pre-load shipping addresses and agents if available
    if (this.Partner.ShippingAddresses && this.Partner.ShippingAddresses.length > 0) {
      this.ShippingAddresses = this.Partner.ShippingAddresses;
    }
    if (this.Partner.PartnerAgents && this.Partner.PartnerAgents.length > 0) {
      this.PartnerAgents = this.Partner.PartnerAgents;
    }

    // Always go to Step 2 (Billing) for existing partners
    this.currentStep = 2;
    this.saveSession();
    this.InitializeNewPartnerWithExistingData();
  }

  InitializeNewPartnerWithExistingData() {
    // Load master data but keep existing Partner data
    var request = {
      User: this.getEffectiveUser(),
      Language: this.getLanguage(),
    };

    this.service.InitializeNewPartner(request).subscribe(response => {
      if (response["Status"] == 1) {
        // Only take Countries and Banks if the API returns non-empty arrays
        const responseCountries = response["Countries"];
        const responseBanks = response["Banks"];

        if (responseCountries && responseCountries.length > 0) {
          this.Countries = [...responseCountries];
        }
        if (responseBanks && responseBanks.length > 0) {
          this.Banks = [...responseBanks];
        }

        // If Countries/Banks are still empty, load them from master data API
        if (this.Countries.length === 0 || this.Banks.length === 0) {
          this.loadMasterData();
        } else {
          // Manually trigger change detection to ensure dropdowns update
          this.cdr.detectChanges();
        }
      }
      this.loading = false;
    }, error => {
      console.error('[RegisterComponent] InitializeNewPartnerWithExistingData() error:', error);
      this.loading = false;
    });
  }

  // ============= STEP NAVIGATION =============

  canNavigateToStep(step: number): boolean {
    // Step 1 is always locked (login/register step) - cannot go back to it
    if (step === 1) {
      return false;
    }

    // For existing partners: can navigate to any step (2, 3, 4)
    if (this.isExistingPartner && this.Partner?.ID) {
      return step >= 2 && step <= 4;
    }

    // For new users: can only go back to previously completed steps
    // or stay on current step
    if (step < this.currentStep && step >= 2) {
      return true;
    }

    return false;
  }

  isStepCompleted(step: number): boolean {
    return this.currentStep > step;
  }

  isStepEnabled(step: number): boolean {
    // Step 1 is special - only enabled when on step 1
    if (step === 1) {
      return this.currentStep === 1;
    }

    // For existing partners: all steps 2-4 are enabled
    if (this.isExistingPartner && this.Partner?.ID) {
      return step >= 2 && step <= 4;
    }

    // For new users: current step and previous completed steps are enabled
    return step <= this.currentStep && step >= 2;
  }

  navigateToStep(step: number) {
    if (!this.canNavigateToStep(step)) {
      return;
    }
    // Don't navigate if already on this step
    if (this.currentStep === step) {
      return;
    }

    this.loading = true;
    this.currentStep = step;
    this.saveSession();

    // Reset dropdown visibility flags when switching tabs
    this.closeAllDropdowns();

    // Initialize the appropriate step
    switch (step) {
      case 2:
        // Step B - Billing: just load master data, Partner data is already loaded
        this.InitializeNewPartnerWithExistingData();
        break;
      case 3:
        // Step C - Shipping: initialize shipping if needed
        if (!this.CurrentShippingAddress?.ID && this.ShippingAddresses?.length > 0) {
          this.CurrentShippingAddress = { ...this.ShippingAddresses[0] };
        }
        this.loading = false;
        break;
      case 4:
        // Step D - Agents: ensure agents are loaded
        if (this.Partner.ShippingAddresses && this.Partner.ShippingAddresses.length > 0) {
          this.ShippingAddresses = this.Partner.ShippingAddresses;
        }
        // Fetch partner agents if not already loaded
        if (!this.PartnerAgents || this.PartnerAgents.length === 0) {
          this.fetchPartnerAgentsForStep4();
        } else {
          this.loading = false;
        }
        break;
      default:
        this.loading = false;
    }
  }

  // ============= STEP B: BILLING INFORMATION =============

  proceedToStepB() {
    this.loading = true;
    this.currentStep = 2;
    this.saveSession();
    this.InitializeNewPartner();
  }

  InitializeNewPartner() {
    var request = {
      User: this.getEffectiveUser(),
      Language: this.getLanguage(),
    };

    this.service.InitializeNewPartner(request).subscribe(response => {
      if (response["Status"] != 1) {
        this.openSnackBar(response["Message"], "OK", "alert-info-snackbar");
      } else {
        this.Partner = response["Partner"];

        // Only update Countries/Banks if the API returns non-empty arrays
        // (InitializeNewPartner may not return these, but loadMasterData already loaded them)
        const responseCountries = response["Countries"];
        const responseBanks = response["Banks"];

        if (responseCountries && responseCountries.length > 0) {
          this.Countries = [...responseCountries];
        }
        if (responseBanks && responseBanks.length > 0) {
          this.Banks = [...responseBanks];
        }

        // If Countries/Banks are still empty, load them from master data API
        if (this.Countries.length === 0 || this.Banks.length === 0) {
          this.loadMasterData();
        }

        // Initialize billing address if not present
        if (!this.Partner.BillingAddress) {
          this.Partner.BillingAddress = {};
        }
        if (!this.Partner.Bank) {
          this.Partner.Bank = {};
        }
        if (!this.Partner.ContactInfo) {
          this.Partner.ContactInfo = {};
        }
        // Manually trigger change detection to ensure dropdowns update
        this.cdr.detectChanges();
      }
      this.loading = false;
    }, error => {
      console.error('[RegisterComponent] InitializeNewPartner() error:', error);
      this.loading = false;
    });
  }

  GetPartnersMasterData() {
    var request = {
      User: this.getEffectiveUser(),
      Language: this.getLanguage(),
    };

    this.service.GetPartnersMasterData(request).subscribe(response => {
      if (response["Status"] == 1) {
        this.Countries = response["Countries"] || [];
        this.Banks = response["Banks"] || [];
      }
    });
  }

  validateBillingInfo(): boolean {
    // Validate mandatory billing fields
    if (!this.Partner.Name || this.Partner.Name.trim() === '') return false;
    if (!this.Partner.VAT || this.Partner.VAT.trim() === '') return false;
    if (!this.Partner.BillingAddress?.Country) return false;
    if (!this.Partner.BillingAddress?.StateProvince || this.Partner.BillingAddress.StateProvince.trim() === '') return false;
    if (!this.Partner.BillingAddress?.City || this.Partner.BillingAddress.City.trim() === '') return false;
    if (!this.Partner.BillingAddress?.AddressLine || this.Partner.BillingAddress.AddressLine.trim() === '') return false;
    if (!this.Partner.BillingAddress?.ZipCode || this.Partner.BillingAddress.ZipCode.trim() === '') return false;
    return true;
  }

  proceedToStepC() {
    if (!this.validateBillingInfo()) {
      this.openSnackBar(this.translate.instant('AllFieldsRequired'), "OK", "alert-info-snackbar");
      return;
    }

    this.loading = true;

    // Set required Company for the API (use DefaultCompany loaded from API, fallback to hardcoded)
    this.Partner.Company = this.DefaultCompany || { ID: 1, Name: "Foxline SRL" };

    // Sanitize Bank: if no bank was selected (empty object), send { ID: 0 } instead of null
    // Backend accesses request.Partner.Bank.ID unconditionally — null would cause NullReferenceException
    if (this.Partner.Bank && !this.Partner.Bank.ID && !this.Partner.Bank.Name) {
      this.Partner.Bank = { ID: 0 };
    }

    // Ensure User has Rights.AssignedCompanies with IsSelected = true
    // so backend ExtractCompany() can resolve the active company for VAT validation
    var user = { ...this.User };
    if (!user.Rights) {
      user.Rights = { AssignedCompanies: [] };
    }
    if (!user.Rights.AssignedCompanies || user.Rights.AssignedCompanies.length === 0) {
      var company = this.DefaultCompany || { ID: 1, Name: 'Foxline SRL' };
      user.Rights.AssignedCompanies = [{ ...company, IsSelected: true }];
    } else {
      // Ensure at least one company has IsSelected = true
      var hasSelected = user.Rights.AssignedCompanies.some(c => c.IsSelected === true);
      if (!hasSelected) {
        user.Rights.AssignedCompanies[0].IsSelected = true;
      }
    }

    var request = {
      User: user,
      Language: this.getLanguage(),
      Partner: this.Partner,
    };

    if (this.isExistingPartner && this.Partner.ID) {
      // Existing partner: update billing info, then navigate to Step 3
      this.service.UpdatePartner(request).subscribe(response => {
        if (response["Status"] != 1) {
          this.openSnackBar(response["Message"], "OK", "alert-info-snackbar");
          this.loading = false;
        } else {
          this.Partner = response["Partner"];
          this.currentStep = 3;
          this.saveSession();
          this.initializeShippingAddress();
        }
      }, () => {
        this.openSnackBar("An error occurred. Please try again.", "OK", "alert-info-snackbar");
        this.loading = false;
      });
    } else {
      // New partner: create first, then navigate to Step 3
      this.service.AddNewPartner(request).subscribe(response => {
        if (response["Status"] != 1) {
          this.openSnackBar(response["Message"], "OK", "alert-info-snackbar");
          this.loading = false;
        } else {
          this.Partner.ID = response["ID"];
          this.isExistingPartner = true;
          if(response["DuplicateDetected"]){
            this.openSnackBar(this.translate.instant("Management.DuplicatePartnerDetected"), "OK", "alert-danger-snackbar");
          }
          this.currentStep = 3;
          this.saveSession();
          this.initializeShippingAddress();
        }
      }, () => {
        this.openSnackBar("An error occurred. Please try again.", "OK", "alert-info-snackbar");
        this.loading = false;
      });
    }
  }

  // ============= STEP C: SHIPPING INFORMATION =============

  initializeShippingAddress() {
    // Generate initial shipping address from billing
    var request = {
      User: this.User,
      Language: this.getLanguage(),
      Partner: this.Partner
    };

    this.service.GenerateInitialShippingAddress(request).subscribe(response => {
      if (response["Status"] != 1) {
        this.openSnackBar(response["Message"], "OK", "alert-info-snackbar");
        // Initialize empty shipping address
        this.CurrentShippingAddress = {
          Country: this.Partner.BillingAddress?.Country || {},
          State: this.Partner.BillingAddress?.StateProvince || '',
          City: this.Partner.BillingAddress?.City || '',
          AddressLine: this.Partner.BillingAddress?.AddressLine || '',
          ZipCode: this.Partner.BillingAddress?.ZipCode || '',
          ContactPerson: this.Partner.ContactInfo?.Name || '',
          ContactCompany: this.Partner.Name || '',
          Email: this.Partner.ContactInfo?.Email || '',
          Phone: this.Partner.ContactInfo?.Phone || '',
          ActiveAddress: true,
          IsSelected: true
        };
      } else {
        this.Partner = response["Partner"];
        var addressesLength = this.Partner.ShippingAddresses?.length || 0;
        if (addressesLength > 0) {
          // Select the first address (or the last one that was added)
          this.selectedShippingAddressIndex = 0;
          this.CurrentShippingAddress = JSON.parse(JSON.stringify(this.Partner.ShippingAddresses[0]));
          // Update ShippingAddresses for Step 4
          this.ShippingAddresses = this.Partner.ShippingAddresses;
        } else {
          // No addresses yet - user must add one
          this.selectedShippingAddressIndex = -1;
          this.CurrentShippingAddress = {};
        }
      }
      this.loading = false;
    });
  }

  validateShippingInfo(): boolean {
    if (!this.CurrentShippingAddress.Country?.ID) return false;
    if (!this.CurrentShippingAddress.State || this.CurrentShippingAddress.State.trim() === '') return false;
    if (!this.CurrentShippingAddress.City || this.CurrentShippingAddress.City.trim() === '') return false;
    if (!this.CurrentShippingAddress.AddressLine || this.CurrentShippingAddress.AddressLine.trim() === '') return false;
    if (!this.CurrentShippingAddress.ZipCode || this.CurrentShippingAddress.ZipCode.trim() === '') return false;
    if (!this.CurrentShippingAddress.ContactPerson || this.CurrentShippingAddress.ContactPerson.trim() === '') return false;
    if (!this.CurrentShippingAddress.ContactCompany || this.CurrentShippingAddress.ContactCompany.trim() === '') return false;
    if (!this.CurrentShippingAddress.Email || this.CurrentShippingAddress.Email.trim() === '') return false;
    if (!this.CurrentShippingAddress.Phone || this.CurrentShippingAddress.Phone.trim() === '') return false;
    return true;
  }

  // Validate shipping address in popup
  validatePopupShippingInfo(): boolean {
    if (!this.editingShippingAddress.Country?.ID) return false;
    if (!this.editingShippingAddress.State || this.editingShippingAddress.State.trim() === '') return false;
    if (!this.editingShippingAddress.City || this.editingShippingAddress.City.trim() === '') return false;
    if (!this.editingShippingAddress.AddressLine || this.editingShippingAddress.AddressLine.trim() === '') return false;
    if (!this.editingShippingAddress.ZipCode || this.editingShippingAddress.ZipCode.trim() === '') return false;
    if (!this.editingShippingAddress.ContactPerson || this.editingShippingAddress.ContactPerson.trim() === '') return false;
    if (!this.editingShippingAddress.ContactCompany || this.editingShippingAddress.ContactCompany.trim() === '') return false;
    if (!this.editingShippingAddress.Email || this.editingShippingAddress.Email.trim() === '') return false;
    if (!this.editingShippingAddress.Phone || this.editingShippingAddress.Phone.trim() === '') return false;
    return true;
  }

  // Select a shipping address from the list
  selectShippingAddress(address: any, index: number) {
    this.selectedShippingAddressIndex = index;
    this.CurrentShippingAddress = JSON.parse(JSON.stringify(address)); // Deep copy
  }

  // Open popup for adding new shipping address
  openNewShippingAddressPopup() {
    this.isNewShippingAddress = true;
    this.editingShippingAddress = {
      ID: 0,
      Country: this.Partner.BillingAddress?.Country || {},
      State: '',
      City: '',
      AddressLine: '',
      ZipCode: '',
      ContactPerson: '',
      ContactCompany: this.Partner.Name || '',
      Email: this.Partner.ContactInfo?.Email || '',
      Phone: this.Partner.ContactInfo?.Phone || '',
      ActiveAddress: true,
      IsSelected: false
    };
    this.showShippingAddressPopup = true;
  }

  // Open popup for editing existing shipping address
  openEditShippingAddressPopup(address: any, index: number) {
    this.isNewShippingAddress = false;
    this.selectedShippingAddressIndex = index;
    this.editingShippingAddress = JSON.parse(JSON.stringify(address)); // Deep copy
    this.showShippingAddressPopup = true;
  }

  // Close the shipping address popup
  closeShippingAddressPopup() {
    this.showShippingAddressPopup = false;
    this.editingShippingAddress = {};
    this.showPopupShippingCountryDropdown = false;
    this.popupShippingCountrySearchTerm = '';
  }

  // Get filtered countries for popup dropdown
  getFilteredPopupShippingCountries(): any[] {
    if (!this.popupShippingCountrySearchTerm) return this.Countries;
    const term = this.popupShippingCountrySearchTerm.toLowerCase();
    return this.Countries.filter(c => c.Name?.toLowerCase().includes(term));
  }

  // Select country in popup dropdown
  selectPopupShippingCountry(country: any) {
    this.editingShippingAddress.Country = country;
    this.showPopupShippingCountryDropdown = false;
    this.popupShippingCountrySearchTerm = '';
  }

  // Close popup country dropdown
  closePopupShippingCountryDropdown() {
    this.showPopupShippingCountryDropdown = false;
    this.popupShippingCountrySearchTerm = '';
  }

  // Save shipping address from popup (Add or Update)
  saveShippingAddressFromPopup() {
    if (!this.validatePopupShippingInfo()) {
      this.openSnackBar(this.translate.instant('AllFieldsRequired'), "OK", "alert-info-snackbar");
      return;
    }

    this.savingShippingAddress = true;

    var request = {
      User: this.User,
      Language: this.getLanguage(),
      ShippingAddress: this.editingShippingAddress,
      PartnerID: this.Partner.ID
    };

    this.service.AddUpdateShippingAddress(request).subscribe(response => {
      if (response["Status"] != 1) {
        this.openSnackBar(response["Message"], "OK", "alert-info-snackbar");
      } else {
        const savedAddress = response["ShippingAddress"];

        if (this.isNewShippingAddress) {
          // Add new address to the Partner's ShippingAddresses array
          if (!this.Partner.ShippingAddresses) {
            this.Partner.ShippingAddresses = [];
          }
          this.Partner.ShippingAddresses.push(savedAddress);
          // Select the newly added address
          this.selectedShippingAddressIndex = this.Partner.ShippingAddresses.length - 1;
          this.CurrentShippingAddress = JSON.parse(JSON.stringify(savedAddress));
          this.openSnackBar(this.translate.instant('AddressAddedSuccessfully') || 'Address added successfully', "OK", "light-info-snackbar");
        } else {
          // Update existing address in the array
          if (this.Partner.ShippingAddresses && this.selectedShippingAddressIndex >= 0) {
            this.Partner.ShippingAddresses[this.selectedShippingAddressIndex] = savedAddress;
            this.CurrentShippingAddress = JSON.parse(JSON.stringify(savedAddress));
          }
          this.openSnackBar(this.translate.instant('AddressUpdatedSuccessfully') || 'Address updated successfully', "OK", "light-info-snackbar");
        }

        // Also update ShippingAddresses for Step 4
        this.ShippingAddresses = this.Partner.ShippingAddresses || [];
        this.saveSession();
        this.closeShippingAddressPopup();
      }
      this.savingShippingAddress = false;
    });
  }

  proceedToStepD() {
    // If no shipping addresses are persisted yet but we have a valid CurrentShippingAddress (e.g. auto-generated from billing),
    // auto-save it before proceeding instead of blocking the user.
    // Also handles case where GenerateInitialShippingAddress returned addresses without persisting them (no ID).
    var hasPersistedAddress = this.Partner.ShippingAddresses?.some(a => a.ID) || false;
    var addressToSave = this.CurrentShippingAddress || (this.Partner.ShippingAddresses?.length > 0 ? this.Partner.ShippingAddresses[0] : null);

    if (!hasPersistedAddress && addressToSave?.Country?.ID && addressToSave?.City) {
      this.loading = true;
      var request = {
        User: this.User,
        Language: this.getLanguage(),
        ShippingAddress: addressToSave,
        PartnerID: this.Partner.ID
      };

      this.service.AddUpdateShippingAddress(request).subscribe(response => {
        if (response["Status"] != 1) {
          this.openSnackBar(response["Message"], "OK", "alert-info-snackbar");
          this.loading = false;
        } else {
          const savedAddress = response["ShippingAddress"];
          // Replace unpersisted addresses with the saved one
          this.Partner.ShippingAddresses = [savedAddress];
          this.selectedShippingAddressIndex = 0;
          this.CurrentShippingAddress = JSON.parse(JSON.stringify(savedAddress));
          this.ShippingAddresses = this.Partner.ShippingAddresses;
          this.currentStep = 4;
          this.saveSession();
          this.loading = false;
        }
      }, () => {
        this.openSnackBar("An error occurred. Please try again.", "OK", "alert-info-snackbar");
        this.loading = false;
      });
      return;
    }

    // Check if user has at least one shipping address - that's all we need
    if (!this.Partner.ShippingAddresses || this.Partner.ShippingAddresses.length === 0) {
      this.openSnackBar(this.translate.instant('PleaseAddAtLeastOneShippingAddress') || 'Please add at least one shipping address', "OK", "alert-info-snackbar");
      return;
    }

    // Update ShippingAddresses for Step 4 and proceed
    this.ShippingAddresses = this.Partner.ShippingAddresses || [];
    this.currentStep = 4;
    this.saveSession();
  }

  // ============= STEP D: AGENTS INFORMATION =============

  fetchPartnerAgentsForStep4() {
    if (!this.Partner?.ID) {
      this.loading = false;
      return;
    }

    var request = {
      User: this.User,
      Language: this.getLanguage() || 'en',
      PartnerID: this.Partner.ID
    };

    this.service.GetPartnerAgents(request).subscribe(response => {
      if (response["Status"] == 1) {
        this.PartnerAgents = response["PartnerAgents"] || [];
        this.Partner.PartnerAgents = this.PartnerAgents;
        // Load default phases for each agent
        this.loadAllAgentsDefaultPhases();
      }
      this.loading = false;
    });
  }

  showAddAgentForm() {
    this.isEditingAgent = false;
    this.editingAgentIndex = -1;
    this.showAgentForm = true;
    this.InitializePartnerAgent();
  }

  editAgent(agent: any, index: number) {
    this.isEditingAgent = true;
    this.editingAgentIndex = index;
    this.showAgentForm = true;

    // Load master data (Departments, ShippingAddresses) if not already loaded
    this.loadAgentMasterData(() => {
      // Create a copy of the agent to edit
      this.PartnerAgent = { ...agent };
    });
  }

  loadAgentMasterData(callback?: () => void) {
    var request = {
      User: this.User,
      Language: this.getLanguage(),
      PartnerID: this.Partner.ID
    };

    this.loading = true;

    this.service.InitializePartnerAgent(request).subscribe(response => {
      if (response["Status"] == 1) {
        this.ShippingAddresses = response["ShippingAddresses"] || [this.CurrentShippingAddress];
        this.Departments = response["Departments"] || [];
      }
      this.loading = false;
      if (callback) {
        callback();
      }
    });
  }

  InitializePartnerAgent() {
    var request = {
      User: this.User,
      Language: this.getLanguage(),
      PartnerID: this.Partner.ID
    };

    this.loading = true;

    this.service.InitializePartnerAgent(request).subscribe(response => {
      if (response["Status"] != 1) {
        this.openSnackBar(response["Message"], "OK", "alert-info-snackbar");
        // Initialize empty agent
        this.PartnerAgent = {
          Name: '',
          Phone: '',
          Email: '',
          Department: null,
          AllocatedShippingAddress: this.CurrentShippingAddress || null
        };
      } else {
        this.PartnerAgent = response["PartnerAgent"];
        this.ShippingAddresses = response["ShippingAddresses"] || [this.CurrentShippingAddress];
        this.Departments = response["Departments"] || [];
      }
      this.loading = false;
    });
  }

  AddUpdatePartnerAgent() {
    if (!this.PartnerAgent.Name || this.PartnerAgent.Name.trim() === '' ||
        !this.PartnerAgent.Email || this.PartnerAgent.Email.trim() === '' ||
        !this.PartnerAgent.Phone || this.PartnerAgent.Phone.trim() === '') {
      this.openSnackBar(this.translate.instant('AllFieldsRequired'), "OK", "alert-info-snackbar");
      return;
    }

    var request = {
      User: this.User,
      Language: this.getLanguage(),
      PartnerAgent: this.PartnerAgent
    };

    this.loading = true;

    this.service.AddUpdatePartnerAgent(request).subscribe(response => {
      if (response["Status"] != 1) {
        this.openSnackBar(response["Message"], "OK", "alert-info-snackbar");
      } else {
        const updatedAgent = response["PartnerAgent"];

        if (this.isEditingAgent && this.editingAgentIndex >= 0) {
          // Update existing agent in the list
          this.PartnerAgents[this.editingAgentIndex] = updatedAgent;
        } else {
          // Add new agent to the list
          this.PartnerAgents.push(updatedAgent);
        }

        this.openSnackBar(response["Message"], "OK", "light-info-snackbar");
        this.showAgentForm = false;
        this.PartnerAgent = null;
        this.isEditingAgent = false;
        this.editingAgentIndex = -1;
      }
      this.loading = false;
    });
  }

  cancelAgentForm() {
    this.showAgentForm = false;
    this.PartnerAgent = null;
    this.isEditingAgent = false;
    this.editingAgentIndex = -1;
  }

  // ============= COMPLETE REGISTRATION =============

  completeRegistration() {
    // ✅ If admin-generated B2B registration link, run the full provisioning chain
    if (this.b2bLinkPartnerID && this.b2bLinkCompanyID) {
      this.completeB2BLinkRegistration();
      return;
    }

    // If we have a Partner ID (existing or newly created), connect User to Partner
    if (this.Partner?.ID && this.User?.Id) {
      this.connectUserToPartner(() => {
        // Update domain with Partner.ID if DomainDBID exists (new domain onboarding)
        this.updateDomainWithPartnerID(() => {
          this.finalizeRegistration();
        });
      });
    } else {
      // No partner to connect, just finalize
      this.finalizeRegistration();
    }
  }

  /**
   * ✅ Full B2B provisioning chain for admin-generated registration links.
   * Sequence: UpdateUserRights → AddUpdateUserAppAccess → GrantUserAppUrlsAccess →
   *           GetUser → AddUpdateB2BUserPartnerConnection → GetCompanyAgents →
   *           AddUpdateB2BUserAgentConnection → finalizeRegistration
   */
  completeB2BLinkRegistration() {
    this.loading = true;
    const userId = this.User.Id;
    const companyId = this.b2bLinkCompanyID;
    const partnerId = this.b2bLinkPartnerID;

    console.log('[B2B Link] Starting full provisioning chain...');

    // Step 1: Assign Customer role on the company from the link
    this.assignCustomerRoleForCompany(companyId);

    // Step 2: Enable B2B App Access (Application ID = 3)
    const appAccessRequest = {
      User: this.SYSTEM_USER,
      Language: this.getLanguage(),
      AppAccess: {
        ID: 0,
        Application: { ID: 3, Name: 'B2B', Access: true },
        UserID: userId,
        Access: true
      }
    };

    this.service.AddUpdateUserAppAccess(appAccessRequest).subscribe(r2 => {
      console.log('[B2B Link] Step 2 - AddUpdateUserAppAccess:', r2["Status"] === 1 ? 'OK' : r2["Message"]);

      // Step 3: Grant all B2B URLs access
      const grantUrlsRequest = {
        User: this.SYSTEM_USER,
        Language: this.getLanguage(),
        UserID: userId,
        Application: { ID: 3, Name: 'B2B', Access: true }
      };

      this.service.GrantUserAppUrlsAccess(grantUrlsRequest).subscribe(r3 => {
        console.log('[B2B Link] Step 3 - GrantUserAppUrlsAccess:', r3["Status"] === 1 ? 'OK' : r3["Message"]);

        // Step 4: Re-fetch user to get fresh data
        const getUserRequest = { id: userId, User: this.SYSTEM_USER, Language: this.getLanguage() };

        this.service.GetUser(getUserRequest).subscribe(r4 => {
          if (r4["Status"] === 1 && r4["User"]) {
            this.User = r4["User"];
            localStorage.setItem('User', JSON.stringify(this.User));
            console.log('[B2B Link] Step 4 - GetUser: OK');
          }

          // Step 5: Connect user to partner
          const partnerConnRequest = {
            User: this.SYSTEM_USER,
            Language: this.getLanguage(),
            PartnerID: partnerId,
            UserID: userId
          };

          this.b2bService.AddUpdateB2BUserPartnerConnection(partnerConnRequest).subscribe(r5 => {
            console.log('[B2B Link] Step 5 - AddUpdateB2BUserPartnerConnection:', r5["Status"] === 1 ? 'OK' : r5["Message"]);

            // Step 6: Get company agents, find paul.dordea@foxline.ro, assign as agent
            const agentsRequest = {
              User: this.SYSTEM_USER,
              Language: this.getLanguage(),
              CompanyID: companyId
            };

            this.service.GetCompanyAgents(agentsRequest).subscribe(r6 => {
              if (r6["Status"] === 1 && r6["Agents"]?.length > 0) {
                const defaultAgent = r6["Agents"].find((a: any) =>
                  a.Email?.toLowerCase() === 'paul.dordea@foxline.ro'
                ) || r6["Agents"][0];

                console.log('[B2B Link] Step 6 - Found agent:', defaultAgent?.Name, 'ID:', defaultAgent?.ID);

                // Step 7: Connect agent to user
                const agentConnRequest = {
                  User: this.SYSTEM_USER,
                  Language: this.getLanguage(),
                  AgentID: defaultAgent.ID,
                  UserID: userId
                };

                this.b2bService.AddUpdateB2BUserAgentConnection(agentConnRequest).subscribe(r7 => {
                  console.log('[B2B Link] Step 7 - AddUpdateB2BUserAgentConnection:', r7["Status"] === 1 ? 'OK' : r7["Message"]);
                  console.log('[B2B Link] ✅ Full provisioning chain COMPLETE');
                  this.loading = false;
                  this.finalizeRegistration();
                }, () => { this.loading = false; this.finalizeRegistration(); });
              } else {
                console.warn('[B2B Link] Step 6 - No agents found for company', companyId);
                this.loading = false;
                this.finalizeRegistration();
              }
            }, () => { this.loading = false; this.finalizeRegistration(); });
          }, () => { this.loading = false; this.finalizeRegistration(); });
        }, () => { this.loading = false; this.finalizeRegistration(); });
      }, () => { this.loading = false; this.finalizeRegistration(); });
    }, () => { this.loading = false; this.finalizeRegistration(); });
  }

  /**
   * Updates the accepted domain record with the Partner.ID after registration is complete.
   * Only runs if DomainDBID > 0 (domain was registered during onboarding without a partner).
   */
  updateDomainWithPartnerID(callback: () => void) {
    if (!this.DomainDBID || this.DomainDBID <= 0 || !this.Partner?.ID) {
      callback();
      return;
    }

    var request = {
      User: this.getEffectiveUser(),
      Language: this.getLanguage(),
      Domain: {
        DBID: this.DomainDBID,
        FKPartnerID: this.Partner.ID
      }
    };

    this.service.AddUpdatePartnerAcceptedDomain(request).subscribe(
      response => {
        if (response["Status"] == 1) {
          console.log('[RegisterComponent] Domain updated with Partner.ID successfully');
        } else {
          console.warn('[RegisterComponent] Failed to update domain:', response["Message"]);
        }
        callback();
      },
      error => {
        console.warn('[RegisterComponent] Error updating domain:', error);
        callback();
      }
    );
  }

  /**
   * Connects the newly registered user to their Partner via B2B User-Partner Connection.
   * This ensures the user is linked to their company/partner in the B2B system.
   * @param callback Function to call after connection attempt (success or failure)
   */
  connectUserToPartner(callback: () => void) {
    var request = {
      User: this.SYSTEM_USER,  // Use system user for authorization
      Language: this.getLanguage(),
      PartnerID: this.Partner.ID,
      UserID: this.User.Id
    };

    this.b2bService.AddUpdateB2BUserPartnerConnection(request).subscribe(
      response => {
        if (response["Status"] == 1) {
          console.log('[RegisterComponent] User-Partner connection established successfully');
        } else {
          console.warn('[RegisterComponent] Failed to establish User-Partner connection:', response["Message"]);
        }
        // Always proceed with finalization (non-blocking)
        callback();
      },
      error => {
        console.warn('[RegisterComponent] Error establishing User-Partner connection:', error);
        // Still proceed with finalization (non-blocking)
        callback();
      }
    );
  }

  /**
   * Final step of registration: clear session, show success message, and navigate.
   */
  finalizeRegistration() {
    // Clear session storage on completion
    this.clearSession();

    // Show the success confirmation card instead of snackbar + redirect
    this.showRegistrationComplete = true;
  }

  navigateToLogin() {
    this.router.navigate(['/login']);
  }

  // ============= STEP NAVIGATION =============

  goBackToStep(step: number) {
    if (step < this.currentStep) {
      this.currentStep = step;
      this.saveSession();
    }
  }

  getStepTitle(): string {
    switch(this.currentStep) {
      case 1: return this.translate.instant('B2BRegister');
      case 2: return this.translate.instant('ViewBillingInformation');
      case 3: return this.translate.instant('ViewShippingInformation');
      case 4: return this.translate.instant('ViewAgentsInformation');
      default: return '';
    }
  }

  // ============= MASTER DATA =============

  loadMasterData() {
    var request = {
      User: this.getEffectiveUser(),
      Language: this.getLanguage()
    };

    this.service.GetMasterData(request).subscribe(response => {
      if (response["Status"] == 1) {
        // Create new array references to ensure Angular detects the change
        this.Countries = [...(response["Countries"] || [])];
        this.Banks = [...(response["Banks"] || [])];
        // Manually trigger change detection to ensure dropdowns update
        this.cdr.detectChanges();
      }
    });
  }

  // ============= SEARCHABLE DROPDOWN HELPERS =============

  /**
   * Returns filtered countries based on search term for Billing Address dropdown
   */
  getFilteredBillingCountries(): any[] {
    if (!this.billingCountrySearchTerm || this.billingCountrySearchTerm.trim() === '') {
      return this.Countries;
    }
    const searchLower = this.billingCountrySearchTerm.toLowerCase().trim();
    return this.Countries.filter(c => c.Name?.toLowerCase().includes(searchLower));
  }

  /**
   * Returns filtered countries based on search term for Shipping Address dropdown
   */
  getFilteredShippingCountries(): any[] {
    if (!this.shippingCountrySearchTerm || this.shippingCountrySearchTerm.trim() === '') {
      return this.Countries;
    }
    const searchLower = this.shippingCountrySearchTerm.toLowerCase().trim();
    return this.Countries.filter(c => c.Name?.toLowerCase().includes(searchLower));
  }

  /**
   * Returns filtered banks based on search term
   */
  getFilteredBanks(): any[] {
    if (!this.bankSearchTerm || this.bankSearchTerm.trim() === '') {
      return this.Banks;
    }
    const searchLower = this.bankSearchTerm.toLowerCase().trim();
    return this.Banks.filter(b => b.Name?.toLowerCase().includes(searchLower));
  }

  /**
   * Select a country for Billing Address and close dropdown
   */
  selectBillingCountry(country: any) {
    this.Partner.BillingAddress.Country = country;
    this.showBillingCountryDropdown = false;
    this.billingCountrySearchTerm = '';
  }

  /**
   * Handler for VAT input blur event.
   * Attempts to identify an existing partner by VAT and auto-populate company data
   * if the user's email domain matches the company name (similarity check).
   * Always fails silently to prevent discovery of existing partners.
   */
  onVATBlur() {
    // Only proceed if we have VAT and country selected
    if (!this.Partner?.VAT || this.Partner.VAT.trim() === '') {
      return;
    }

    if (!this.Partner?.BillingAddress?.Country?.ID) {
      return;
    }

    // Only proceed if user is logged in
    if (!this.User?.Email) {
      return;
    }

    // Skip if already in existing partner mode (data already loaded)
    if (this.isExistingPartner) {
      return;
    }

    // Use DefaultCompany.ID (loaded from API), fallback to 1
    const companyId = this.DefaultCompany?.ID || 1;

    const request = {
      User: this.getEffectiveUser(),
      Language: this.getLanguage(),
      VAT: this.Partner.VAT.trim(),
      CountryCode: this.Partner.BillingAddress.Country.ShortName || '',
      CountryID: this.Partner.BillingAddress.Country.ID || 0,
      UserEmail: this.User.Email,
      CompanyID: companyId
    };

    console.log('[RegisterComponent] onVATBlur() - calling IdentifyPartnerByVAT with:', request);

    this.b2bService.IdentifyPartnerByVAT(request).subscribe(response => {
      console.log('[RegisterComponent] IdentifyPartnerByVAT response:', response);

      if (response["Status"] === 1 && response["PartnerIdentified"] === true && response["Partner"]) {
        const identifiedPartner = response["Partner"];
        console.log('[RegisterComponent] Partner identified, auto-populating data:', identifiedPartner);

        // Auto-populate partner fields (company info)
        if (identifiedPartner.Name) {
          this.Partner.Name = identifiedPartner.Name;
        }
        if (identifiedPartner.UPN) {
          this.Partner.UPN = identifiedPartner.UPN;
        }
        if (identifiedPartner.COCRegNumber) {
          this.Partner.COCRegNumber = identifiedPartner.COCRegNumber;
        }

        // Auto-populate billing address
        if (identifiedPartner.BillingAddress) {
          const addr = identifiedPartner.BillingAddress;
          if (addr.AddressLine) {
            this.Partner.BillingAddress.AddressLine = addr.AddressLine;
          }
          if (addr.City) {
            this.Partner.BillingAddress.City = addr.City;
          }
          // Map StateProvince (backend uses StateProvince, UI uses StateProvince)
          if (addr.StateProvince) {
            this.Partner.BillingAddress.StateProvince = addr.StateProvince;
          }
          // Map ZipCode (backend uses ZipCode, UI uses ZipCode)
          if (addr.ZipCode) {
            this.Partner.BillingAddress.ZipCode = addr.ZipCode;
          }
          if (addr.District) {
            this.Partner.BillingAddress.District = addr.District;
          }
          if (addr.Country?.ID) {
            // Find and set the country from our loaded list
            const matchedCountry = this.Countries.find(c => c.ID === addr.Country.ID);
            if (matchedCountry) {
              this.Partner.BillingAddress.Country = matchedCountry;
            }
          }
        }

        // Auto-populate bank (copy ID, Name, Swift AND BankAccount)
        if (identifiedPartner.Bank?.ID) {
          const matchedBank = this.Banks.find(b => b.ID === identifiedPartner.Bank.ID);
          if (matchedBank) {
            // Copy bank reference and add the BankAccount from the identified partner
            this.Partner.Bank = { ...matchedBank };
            // Copy the bank account number from the identified partner
            if (identifiedPartner.Bank.BankAccount) {
              this.Partner.Bank.BankAccount = identifiedPartner.Bank.BankAccount;
            }
          }
        }

        // Auto-populate contact info
        if (identifiedPartner.ContactInfo) {
          const contact = identifiedPartner.ContactInfo;
          // Map Name (contact person name)
          if (contact.Name) {
            this.Partner.ContactInfo.Name = contact.Name;
          }
          if (contact.Email) {
            this.Partner.ContactInfo.Email = contact.Email;
          }
          if (contact.Phone) {
            this.Partner.ContactInfo.Phone = contact.Phone;
          }
        }

        // Trigger change detection and save session
        this.cdr.detectChanges();
        this.saveSession();

        // Show success notification
        this.openSnackBar(
          this.translate.instant('CompanyIdentifiedSuccessfully') || 'Company data found and auto-filled',
          "OK",
          "alert-success-snackbar"
        );
      }
      // Else: silently do nothing (no partner found or similarity check failed)
    }, error => {
      // Silently fail on error - do not expose any information
      console.log('[RegisterComponent] IdentifyPartnerByVAT error (silently ignored):', error);
    });
  }

  /**
   * Select a country for Shipping Address and close dropdown
   */
  selectShippingCountry(country: any) {
    this.CurrentShippingAddress.Country = country;
    this.showShippingCountryDropdown = false;
    this.shippingCountrySearchTerm = '';
  }

  /**
   * Select a bank and close dropdown
   */
  selectBank(bank: any) {
    this.Partner.Bank = bank;
    this.showBankDropdown = false;
    this.bankSearchTerm = '';
  }

  /**
   * Close Billing Country dropdown and reset search
   */
  closeBillingCountryDropdown() {
    this.showBillingCountryDropdown = false;
    this.billingCountrySearchTerm = '';
  }

  /**
   * Close Shipping Country dropdown and reset search
   */
  closeShippingCountryDropdown() {
    this.showShippingCountryDropdown = false;
    this.shippingCountrySearchTerm = '';
  }

  /**
   * Close Bank dropdown and reset search
   */
  closeBankDropdown() {
    this.showBankDropdown = false;
    this.bankSearchTerm = '';
  }

  closeAllDropdowns() {
    this.showBillingCountryDropdown = false;
    this.showBankDropdown = false;
    this.showShippingCountryDropdown = false;
    this.showPopupShippingCountryDropdown = false;
    this.showAgentShippingDropdown = false;
    this.showDepartmentDropdown = false;
  }

  // ============= ADD NEW BANK =============

  toggleAddBankForm() {
    this.showAddBankForm = !this.showAddBankForm;
    this.newBankName = '';
    this.newBankSwift = '';
  }

  addNewBank() {
    if (!this.newBankName || this.newBankName.trim() === '') {
      this.openSnackBar(this.translate.instant('BankNameRequired'), "OK", "alert-info-snackbar");
      return;
    }

    if (!this.newBankSwift || this.newBankSwift.trim() === '') {
      this.openSnackBar(this.translate.instant('BankSwiftRequired'), "OK", "alert-info-snackbar");
      return;
    }

    var request = {
      User: this.getEffectiveUser(),
      Language: this.getLanguage(),
      Bank: {
        Name: this.newBankName.trim(),
        Swift: this.newBankSwift.trim()
      }
    };

    this.addingBank = true;

    this.service.AddBank(request).subscribe(response => {
      if (response["Status"] != 1) {
        this.openSnackBar(response["Message"], "OK", "alert-info-snackbar");
        this.addingBank = false;
      } else {
        // Get the newly added bank's ID from the response
        const newBankId = response["Bank"]?.ID || response["ID"];

        this.showAddBankForm = false;
        this.newBankName = '';
        this.newBankSwift = '';
        this.openSnackBar(this.translate.instant('BankAddedSuccessfully'), "OK", "light-info-snackbar");

        // Refresh banks from API to get the complete list including the new bank
        // Then select the new bank by its returned ID
        this.refreshBanksAndSelect(newBankId);
      }
    });
  }

  /**
   * Refresh banks from API and select a specific bank by ID
   */
  refreshBanksAndSelect(bankIdToSelect: string) {
    var request = {
      User: this.getEffectiveUser(),
      Language: this.getLanguage()
    };

    this.service.GetMasterData(request).subscribe(response => {
      if (response["Status"] == 1) {
        this.Banks = [...(response["Banks"] || [])];

        // Find and select the bank with the specified ID
        if (bankIdToSelect) {
          const selectedBank = this.Banks.find(b => b.ID === bankIdToSelect);
          if (selectedBank) {
            this.Partner.Bank = { ...selectedBank };
          }
        }

        this.cdr.detectChanges();
        this.saveSession();
      }
      this.addingBank = false;
    }, () => {
      this.addingBank = false;
    });
  }

  cancelAddBank() {
    this.showAddBankForm = false;
    this.newBankName = '';
  }

  // ============= SESSION PERSISTENCE =============

  saveSession() {
    const sessionData = {
      currentStep: this.currentStep,
      User: this.User,
      Partner: this.Partner,
      CurrentShippingAddress: this.CurrentShippingAddress,
      PartnerAgents: this.PartnerAgents,
      DomainDBID: this.DomainDBID
    };
    sessionStorage.setItem(this.SESSION_KEY, JSON.stringify(sessionData));
  }

  restoreSession() {
    const savedSession = sessionStorage.getItem(this.SESSION_KEY);
    if (savedSession) {
      try {
        const sessionData = JSON.parse(savedSession);

        // Restore User first
        if (sessionData.User) {
          this.User = sessionData.User;
          localStorage.setItem('User', JSON.stringify(this.User));
        }

        // Restore Partner with nested object initialization
        if (sessionData.Partner) {
          this.Partner = sessionData.Partner;
          // Ensure nested objects exist
          if (!this.Partner.BillingAddress) {
            this.Partner.BillingAddress = {};
          }
          if (!this.Partner.Bank) {
            this.Partner.Bank = {};
          }
          if (!this.Partner.ContactInfo) {
            this.Partner.ContactInfo = {};
          }
        }

        // Restore Shipping Address
        if (sessionData.CurrentShippingAddress) {
          this.CurrentShippingAddress = sessionData.CurrentShippingAddress;
        }

        // Restore Agents
        if (sessionData.PartnerAgents) {
          this.PartnerAgents = sessionData.PartnerAgents;
        }

        // Restore DomainDBID
        if (sessionData.DomainDBID) {
          this.DomainDBID = sessionData.DomainDBID;
        }

        // Restore step ONLY if we have valid User (required for Step 2+)
        // Also require Partner with BillingAddress for Step 2+
        if (sessionData.currentStep && sessionData.currentStep > 1 && this.User && this.Partner) {
          this.currentStep = sessionData.currentStep;
        } else if (sessionData.currentStep && sessionData.currentStep > 1) {
          // Invalid session state - clear and start fresh
          this.clearSession();
          this.currentStep = 1;
        }
      } catch (e) {
        // If parse fails, clear corrupted session
        this.clearSession();
      }
    }
  }

  clearSession() {
    sessionStorage.removeItem(this.SESSION_KEY);
  }

  // ============= RESTART PROCESS =============

  showRestartConfirmation() {
    this.showRestartConfirm = true;
  }

  cancelRestart() {
    this.showRestartConfirm = false;
  }

  confirmRestart() {
    // Clear all session data
    this.clearSession();

    // Reset all form data
    this.currentStep = 1;
    this.User = null;
    this.Partner = {
      BillingAddress: {},
      Bank: {},
      ContactInfo: {}
    };
    this.CurrentShippingAddress = {};
    this.PartnerAgents = [];
    this.PartnerAgent = null;
    this.showAgentForm = false;
    this.showExistingUserLogin = false;
    this.isRegistering = false;
    this.ShowMFA = false;
    this.showRestartConfirm = false;
    this.showCheckEmail = false;
    this.showPartnerSelection = false;
    this.showRegistrationComplete = false;
    this.domainPartners = [];
    this.DomainDBID = 0;

    // Reset registration form
    this.signin.reset();

    this.openSnackBar(this.translate.instant('ProcessRestarted'), "OK", "light-info-snackbar");
  }

  // ========== AGENT NOTIFICATION PHASES ==========

  loadAllAgentsDefaultPhases(): void {
    if (!this.PartnerAgents?.length) return;
    this.PartnerAgents.forEach((agent: any) => {
      if (agent.ID > 0) {
        const req = { User: this.getEffectiveUser(), Language: this.getLanguage() || 'en', AgentID: agent.ID };
        this.service.GetPartnerAgentDefaultPhases(req).subscribe((res: any) => {
          if (res.Status === 1) {
            agent.OrderPhases = res.Phases || [];
          }
        });
      }
    });
  }

  openDefaultPhasesPopup(agent: any): void {
    if (!agent?.ID || agent.ID <= 0) return;
    this.selectedDefaultPhaseAgent = agent;

    // Load masterdata if needed
    if (this.defaultPhasesMasterData.length === 0) {
      const req = { User: this.getEffectiveUser(), Language: this.getLanguage() || 'en' };
      this.service.GetPartnersMasterData(req).subscribe((response: any) => {
        if (response.Status === 1) {
          this.defaultPhasesMasterData = response.PartnerAgentOrderPhases || [];
        }
      });
    }

    // Load current default phases for this agent
    const request = { User: this.getEffectiveUser(), Language: this.getLanguage() || 'en', AgentID: agent.ID };
    this.loading = true;
    this.service.GetPartnerAgentDefaultPhases(request).subscribe((response: any) => {
      this.loading = false;
      if (response.Status === 1) {
        agent.OrderPhases = response.Phases || [];
        this.selectedDefaultPhaseIDs = (response.Phases || []).map((p: any) => p.ID);
      }
      this.showDefaultPhasesPopup = true;
    });
  }

  closeDefaultPhasesPopup(): void {
    this.showDefaultPhasesPopup = false;
  }

  isDefaultPhaseSelected(phase: any): boolean {
    return this.selectedDefaultPhaseIDs.includes(phase.ID);
  }

  toggleDefaultPhase(phase: any): void {
    const idx = this.selectedDefaultPhaseIDs.indexOf(phase.ID);
    if (idx >= 0) {
      this.selectedDefaultPhaseIDs.splice(idx, 1);
    } else {
      this.selectedDefaultPhaseIDs.push(phase.ID);
    }
  }

  saveDefaultPhases(): void {
    if (!this.selectedDefaultPhaseAgent || this.savingDefaultPhases) return;

    const request = {
      User: this.getEffectiveUser(),
      Language: this.getLanguage() || 'en',
      AgentID: this.selectedDefaultPhaseAgent.ID,
      PhaseIDs: this.selectedDefaultPhaseIDs
    };

    this.savingDefaultPhases = true;
    this.service.AddUpdatePartnerAgentDefaultPhases(request).subscribe((response: any) => {
      this.savingDefaultPhases = false;
      if (response.Status === 1) {
        this.selectedDefaultPhaseAgent.OrderPhases = response.Phases || [];
        // Sync on PartnerAgents list
        const match = this.PartnerAgents.find((a: any) => a.ID === this.selectedDefaultPhaseAgent.ID);
        if (match) match.OrderPhases = response.Phases || [];
        this.openSnackBar(response.Message || 'Default phases saved', 'OK', 'light-info-snackbar');
        this.closeDefaultPhasesPopup();
      } else {
        this.openSnackBar(response.Message || 'Error saving default phases', 'OK', 'alert-info-snackbar');
      }
    });
  }
}
